// externals
import React from 'react';

// libraries
import { Action } from '@interstate/components/Action';
import { Typography } from '@interstate/components/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { ShieldCheckIcon } from '@interstate/components/Icons';
import { Tooltip } from '@interstate/components/Tooltip';
import { Box } from '@interstate/components/Box';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as customerRiskAndCoverageActionCreators from '@makemydeal/dr-dash-store/actions/customerRiskAndCoverageActionCreators';
import * as editCustomerActionCreators from '@makemydeal/dr-dash-store/actions/editCustomerActionCreators';
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { VppDealType } from '@makemydeal/dr-platform-types';

// components
import CustomerRiskAndCoverageSlideOut from '../vehicleInsurance/CustomerRiskAndCoverageSlideOut';
import ActionWithErrorBadge from './ActionWithErrorBadge';
import { VEHICLE_INSURANCE } from '@makemydeal/dr-shared-types';

type VehicleInsuranceLinkProps = {
    disabled?: boolean;
};

export const VehicleInsuranceLink: React.FC<VehicleInsuranceLinkProps> = ({ disabled = false }) => {
    const dispatch = useDispatch();
    const isCreditProviderRouteOne = useSelector(dealerSelectors.isCreditProviderRouteOne);
    const { dealType } = useSelector(vehicleProtectionSelectors.getVppSurchargesOverrides);
    const vehicleInsuranceInfo = useSelector(baseDealSelectors.getVehicleInsuranceInfo);
    const isShowInsuranceInUnifiedCustomerCardEnabled = useSelector(
        featureToggleSelectors.isShowInsuranceInUnifiedCustomerCardEnabled
    );
    const isActivitiesCustomerCardEnabled = useSelector(featureToggleSelectors.isActivitiesCustomerCardEnabled);
    const { verifiedBy } = vehicleInsuranceInfo || {};
    return (
        <>
            <ActionWithErrorBadge entityName={VEHICLE_INSURANCE} xOffset="-1px" yOffset="5px">
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '4px',
                        marginTop: '2px'
                    }}
                >
                    {verifiedBy && (
                        <Tooltip
                            data-testid="insurance-verified-tooltip"
                            toolTipContent={`Verified by ${verifiedBy}`}
                            position="top-start"
                            size="sm"
                        >
                            <ShieldCheckIcon data-testid="insurance-verified-icon" color="green" />
                        </Tooltip>
                    )}
                    <Typography
                        variant="h3"
                        tag="span"
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '.4rem',
                            alignItems: 'center',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <Action
                            size="md"
                            data-testid="vehicle-insurance-btn"
                            disabled={disabled}
                            onClick={() => {
                                if (isShowInsuranceInUnifiedCustomerCardEnabled && isActivitiesCustomerCardEnabled) {
                                    dispatch(editCustomerActionCreators.showEditInsuranceSlideout());
                                    return;
                                }
                                dispatch(baseDealActionCreators.setCustomerRiskAndCoverageTab('insurance'));
                                dispatch(baseDealActionCreators.setCustomerRiskAndCoverageSlideOutStatus(true));
                                dispatch(customerRiskAndCoverageActionCreators.customerRiskAndCoverageSlideOutButton());
                            }}
                        >
                            <span style={{ fontWeight: 400 }}>Insurance</span>
                        </Action>
                    </Typography>
                </Box>
            </ActionWithErrorBadge>

            {(isCreditProviderRouteOne || dealType === VppDealType.CASH) && <CustomerRiskAndCoverageSlideOut mode="insurance" />}
        </>
    );
};
